// src/lib/featureCatalog.ts
//
// Canonical feature catalog. This is the single source of truth for "what
// features exist in this app" — both at the page level (a landing-page
// tile) and (in PR3) inside individual pages (sub-sections).
//
// Each entry:
//   - `key`         — stable identifier; matches admin.duckdb `features.feature_key`
//                     and the agent's effectiveFeatures response. Lowercase
//                     kebab-case. Top-level keys map 1:1 to LandingPage tile ids.
//   - `label`       — short display name in System Admin → Features
//   - `description` — one-liner explaining what the feature controls
//   - `category`    — visual grouping in the Features tab UI
//   - `parent`      — optional parent feature key (sub-features only). If
//                     present, the child is only meaningful when the parent
//                     is also enabled. PR3 will fill these in.
//   - `defaultEnabled` — fallback when neither the agent's `features` table
//                        nor any user override has an entry. Lets brand-new
//                        tenants get a sensible set of features without
//                        any seeding step.
//   - `sysadminOnly` — convenience flag for the System Admin tile, which
//                      stays gated by the GeniReports Admin Auth0 role
//                      because it manages OTHER tenants (not a per-tenant
//                      feature in the usual sense).
//
// Adding a feature: add an entry here. No DB migration needed. Sysadmin can
// override per tenant + per user via the existing Features tab; if they
// haven't, the `defaultEnabled` value applies. PR3 will add sub-feature
// entries (analytics.sales, route-reports.send-now, etc.).

export interface FeatureCatalogEntry {
  key: string;
  label: string;
  description: string;
  category: 'reports' | 'integrations' | 'operations' | 'account' | 'admin';
  parent?: string;
  defaultEnabled: boolean;
  sysadminOnly?: boolean;
}

/**
 * Page-level features. Each tile on LandingPage corresponds to one entry
 * here whose `key` matches the tile id. PR3 will add sub-feature entries
 * with `parent` set to one of these keys.
 */
export const FEATURE_CATALOG: FeatureCatalogEntry[] = [
  {
    key: 'analytics',
    label: 'Analytics Dashboard',
    description: 'Top-level access to the Analytics hub. Sub-features control which sections inside the hub the user can see.',
    category: 'reports',
    defaultEnabled: true,
  },
  // ── Analytics sub-sections (child features under `analytics`) ──
  {
    key: 'analytics.sales',
    label: 'Analytics → Sales',
    description: 'Sales detail view (item-level comparison, P1 vs P2, exports). Affects both the sidebar nav item and the Overview summary card.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: true,
  },
  {
    key: 'analytics.sales.by_rep',
    label: 'Analytics → Sales → By Rep',
    description: 'Per-customer scorecard filtered to one sales rep: last-week vs 12-week averages, returns, AR aging, and per-SKU pricing. Opt-in per tenant (built first for Devash).',
    category: 'reports',
    parent: 'analytics.sales',
    defaultEnabled: false,
  },
  {
    key: 'analytics.assistant',
    label: 'Analytics → Assistant',
    description: 'Conversational AI analyst that answers questions about your data by running the same reports the dashboards use. Opt-in per tenant; requires an Anthropic API key configured on the agent.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.ar',
    label: 'Analytics → AR',
    description: 'AR aging detail and per-customer drilldown.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: true,
  },
  {
    key: 'analytics.customers',
    label: 'Analytics → Customers',
    description: 'Analytics → Customers → Detail: every customer\'s sales vs the prior period, GP, invoices, average ticket, last sale and days between orders, with a status (new / growing / steady / declining / lapsed / inactive), segment table, monthly active / new / lost chart and Excel / CSV export. Needs agent 3.22.0+.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.margins',
    label: 'Analytics → Margins',
    description: 'Margin analysis next to GP: GP % by item, customer, salesman, price level or item (sub)category against a target %, erosion vs the prior period in points, and the invoice lines sold below cost. Excel / CSV export. Needs agent 3.22.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.inventory',
    label: 'Analytics → Inventory',
    description: 'Inventory replenishment (Days of Supply, velocity, alerts). Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  // ── Inventory sub-features (children of `analytics.inventory`) ──
  // All default ON so existing tenants see no change; turn OFF per tenant/user
  // for those who want a simpler view. Gated inside InventoryTab.
  {
    key: 'analytics.inventory.briefing',
    label: 'Analytics → Inventory → Today’s Briefing',
    description: 'The AI "Today’s briefing" banner at the top of the Inventory page (a one-line headline from the daily insight). Turn off to hide the banner; the full Insights tab is controlled separately.',
    category: 'reports',
    parent: 'analytics.inventory',
    defaultEnabled: true,
  },
  {
    key: 'analytics.inventory.transitions',
    label: 'Analytics → Inventory → Transitions',
    description: 'The Transitions feed (chronological bucket/activity changes) — the tab, its "N transitions last cycle" status count, and per-SKU transition history in the drilldown. Turn off to hide transitions everywhere on the page.',
    category: 'reports',
    parent: 'analytics.inventory',
    defaultEnabled: true,
  },
  {
    key: 'analytics.inventory.insights',
    label: 'Analytics → Inventory → Insights',
    description: 'The full AI Insights tab (daily briefing) inside the Inventory page. Requires an Anthropic API key on the agent (Insights.Enabled). Turn off to hide the tab.',
    category: 'reports',
    parent: 'analytics.inventory',
    defaultEnabled: true,
  },
  {
    key: 'analytics.inventory.velocity',
    label: 'Analytics → Inventory → Velocity (Vel/d)',
    description: 'The per-day velocity (Vel/d) columns and tiles across the action queue, transitions feed, and SKU drilldown. Turn off to hide velocity figures for a simpler view.',
    category: 'reports',
    parent: 'analytics.inventory',
    defaultEnabled: true,
  },
  {
    key: 'analytics.inventory.acceleration',
    label: 'Analytics → Inventory → Acceleration / Trend',
    description: 'The momentum "Trend" column (acceleration arrows), the drilldown acceleration tile, and the acceleration filters in the Transitions feed. Turn off to hide acceleration signals.',
    category: 'reports',
    parent: 'analytics.inventory',
    defaultEnabled: true,
  },
  {
    key: 'analytics.inventory.decay',
    label: 'Analytics → Inventory → Decay',
    description: 'The drilldown "Decay (v30/v90)" tile and the decay filters in the Transitions feed. Turn off to hide decay signals.',
    category: 'reports',
    parent: 'analytics.inventory',
    defaultEnabled: true,
  },
  {
    key: 'analytics.ap',
    label: 'Analytics → A/P (Aging, Bills)',
    description: 'Accounts payable. Aging of the open bills in QuickBooks Online by vendor, by bill date or by days past due, with a payment schedule (past due / this week / next week / 30 days); bills are paid in QuickBooks, so this needs the QBO module connected. Bills: every purchase order in Advantage (the bill pushed to QuickBooks) with received / not received / sent-to-QuickBooks and its lines, ordered vs received. Excel / CSV export. Needs agent 3.24.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.vendors',
    label: 'Analytics → A/P (Vendor Spend, Price Changes, Scorecard)',
    description: 'Purchasing analysis: vendor spend by month / quarter / year vs the year before with item drill-down; vendor price changes per item with cost impact and margin squeeze (cost up more than the sell price); vendor scorecard — fill rate, on-time, lead time, billed vs PO cost, open and past-due A/P. Needs agent 3.24.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.qbodiff',
    label: 'Analytics → QuickBooks Diff',
    description: 'Advantage vs QuickBooks Online, document by document, for bills (A/P tab) and invoices (A/R tab): missing on either side, amount, open balance, date or vendor/customer differences. QuickBooks Online tenants only. Needs agent 3.24.0+.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.araging',
    label: 'Analytics → A/R Aging by Date',
    description: 'A/R aging computed from open invoices, by invoice date or by days past due (terms), with the collection schedule and Excel / CSV export. Needs agent 3.24.0+.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.purchasing',
    label: 'Analytics → Purchasing Pipeline',
    description: 'Container/CBM editor for importers — plan shipping containers, back-write to Advantage shpschd, track partial receipts. Opt-in per tenant; built for Blue Sky 2026-06-24.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.orders',
    label: 'Analytics → Order Sheet',
    description: 'Line-level order browser — filter open/invoiced/on-hold orders by date, customer, item, and category; summary tiles + CSV export. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.trends',
    label: 'Analytics → Sales Trends',
    description: 'Bucketed sales time series (week/month/quarter/year/season) with the shared analytics filter set, chart + table + CSV export. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.gaps',
    label: 'Analytics → Lost Business',
    description: 'Churn finder — customers who stopped buying, items a customer dropped, and customers who never bought a target item. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.compare7v28',
    label: 'Analytics → 7 vs 28 Days',
    description: 'This-week vs trailing-4-week-average comparison by category, area, salesman, or customer — spot who is up or down right now. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.yoy',
    label: 'Analytics → Overview (YoY)',
    description: 'Multi-year year-over-year matrix — sales by period (week/month/quarter/year) across years, with Hebrew or Gregorian calendar alignment. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.arinsights',
    label: 'Analytics → A/R Insights',
    description: 'A/R aging history over time (from daily snapshots) plus payment behavior vs terms — how much comes in before, on, or after terms. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.opsaudit',
    label: 'Analytics → Operations Audit',
    description: 'Operational exception finder — shipped-not-invoiced orders, voided orders (including voided after shipping), and vendor short-shipments. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.salespayments',
    label: 'Analytics → Sales vs Payments',
    description: 'Sales billed vs payments received per period (week/month/quarter, Gregorian or Hebrew calendar) with a breakdown by customer category, salesman, or area. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.filterpane',
    label: 'Analytics → Filter Pane',
    description: 'Power BI-style slicers shared across the YoY, 7 vs 28, GP and Trends tabs: Sales Net / Gross / Returns, price level, area, customer category, credit hold, salesman / salesman 2 / route (from the invoice line), item category, vendor, item type, discontinued, and day of week. Also adds the YoY "%" switch (YoY / Margin / % of Total). Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.invoices',
    label: 'Analytics → Invoices & Penetration',
    description: 'Category × month matrix of invoice count, average ticket ($ per invoice), % of dollars, customers buying, penetration (% of buying customers) and $ per customer. Gregorian or Hebrew calendar. Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.custweeks',
    label: 'Analytics → Customer Weeks',
    description: 'What each customer buys, item category by item category, over the last 4–26 weeks (qty or $), with gaps highlighted so reps spot a category that stopped. Gregorian or Hebrew week labels. Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.shortmatrix',
    label: 'Analytics → Short Shipped by Month',
    description: 'Ops Audit → Short Shipped gains a Customer-orders source (invoiced orders that shipped less than ordered), customer rows (area, category, price level, salesman) and a by-month qty / $ matrix, as in the PBI page. Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.arsalesman',
    label: 'Analytics → A/R by Salesman',
    description: 'A/R Aging gains a rollup by salesman, area or customer category (balance, aging buckets, % over 90) that follows the tab filters; click a row to filter the customer list. A/R Aging History can be split the same way (balance per group over time). Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.ccfee',
    label: 'Analytics → CC Fees',
    description: 'Card fees charged to customers in Advantage vs processing fees paid in QuickBooks, by month — net and recovery %. The paid side needs the QuickBooks module. Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.arfocus',
    label: 'Analytics → A/R Collections',
    description: 'AR Focus (billed and collected in a selection window, open A/R inside vs past the window, A/R today and over-90 share, by area / salesman / category) plus payments by the aging bucket they cleared from (pie, weekly stacks by bucket or terms, days-range filter). Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.gp',
    label: 'Analytics → GP Analysis',
    description: 'Gross-profit analysis by item/customer dimension — GP $, GP %, GP per unit, and (when cube data exists) GP per cube and per pallet. GP uses the cost on each invoice line at time of sale (agent 3.20.0+; older agents estimate from current average cost). Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.customercounts',
    label: 'Analytics → Customer Counts',
    description: 'Distinct buying customers per month by customer category — a month-over-month activity matrix with averages and totals. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.drill',
    label: 'Analytics → Drill-down',
    description: 'Overview (YoY) and GP Analysis rows expand in place: an item category opens its items, and a customer category, price level, salesman or area opens its customers — like the Power BI expand (+) on a matrix row. Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.arledger',
    label: 'Analytics → Customer A/R Ledger',
    description: 'A "Ledger" button on the A/R Detail customer drilldown: every invoice, credit and payment in date order with a running balance from the opening balance, tied to the open invoice balances (any difference is shown). Excel / CSV export. Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.transfers',
    label: 'Analytics → Transfer Exceptions',
    description: 'Orders → Transfers: warehouse transfers where the item received differs from the item sent, quantity differences, lines transmitted from staging that never posted (or posted with a different quantity), and batches left unposted. Filters by warehouse, item category and subcategory; $ impact at average cost; Excel / CSV export. Reads Advantage TransferInvt / StagingTransferInvt. Needs agent 3.21.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.ordersnapshot',
    label: 'Analytics → Daily Order Snapshot',
    description: 'Panel under Daily Snapshot: open (not yet invoiced) orders for a delivery date by item category and by driver, with on-hold counts. Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'analytics.dailysnapshot',
    label: 'Analytics → Daily Snapshot',
    description: 'One-day business snapshot — sales, estimated GP, invoices, customers, and payments for a chosen day, with category and top-customer breakdowns. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'ai_orders',
    label: 'AI Orders',
    description: 'Review queue for AI-captured orders (voice + email) — verify lines and pricing, assign a customer, approve or reject before posting to Advantage. Opt-in per tenant.',
    category: 'reports',
    parent: 'analytics',
    defaultEnabled: false,
  },
  {
    key: 'ai_orders.use_onhand',
    label: 'AI Orders → Use On-Hand Availability',
    description: 'When on, the phone ordering assistant sees stock on hand — it tells the caller when a requested quantity exceeds availability and asks how to proceed. When off, stock levels are hidden from the assistant and never mentioned; the office handles shortages after the fact.',
    category: 'reports',
    parent: 'ai_orders',
    defaultEnabled: true,
  },
  {
    key: 'financials',
    label: 'Financials',
    description: 'P&L and business-ops dashboard sourced from QuickBooks Desktop — profit & loss, expense breakdown, A/R and A/P aging. Opt-in per tenant.',
    category: 'reports',
    defaultEnabled: false,
  },
  {
    key: 'financials.payroll',
    label: 'Financials → Payroll & Time Cards',
    description: 'Payroll summary and time-card detail sections inside Financials. On by default when Financials is enabled; turn off per tenant/user to hide compensation data.',
    category: 'reports',
    parent: 'financials',
    defaultEnabled: true,
  },
  {
    key: 'financials.balancesheet',
    label: 'Financials → Balance Sheet',
    description: 'Balance sheet statement (assets, liabilities, equity) inside Financials. On by default when Financials is enabled.',
    category: 'reports',
    parent: 'financials',
    defaultEnabled: true,
  },
  {
    key: 'financials.budget',
    label: 'Financials → Budget',
    description: 'Budget vs actual section inside Financials — generate a budget from a base year, edit per-account monthly amounts, and track variance. On by default when Financials is enabled.',
    category: 'reports',
    parent: 'financials',
    defaultEnabled: true,
  },
  {
    key: 'financials.qbo',
    label: 'Financials → QuickBooks Online',
    description: 'For tenants on QuickBooks Online: Financials shows the P&L and Balance Sheet straight from QuickBooks Online reports, with account levels 1–4, any date range, month/quarter columns, accrual or cash, and the balance sheet as of any date. Hides the QuickBooks Desktop-only sections. Needs agent 3.24.0+ with the QBO module connected. Tenant-wide, opt-in.',
    category: 'reports',
    parent: 'financials',
    defaultEnabled: false,
  },
  {
    key: 'financials.kpipanel',
    label: 'Financials → COGS & Expense KPIs',
    description: 'Overview panel: income, COGS, payroll and overhead expenses as $ and % of income for the same month (or YTD) across three years, with net income vs budget. Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'financials',
    defaultEnabled: false,
  },
  {
    key: 'financials.pnlcompare',
    label: 'Financials → P&L Compare Columns',
    description: 'A "Compare" column layout on the P&L — current period $ and % of income, prior year $ and %, change vs prior year, budget and variance vs budget. Needs agent 3.20.0+. Opt-in per tenant.',
    category: 'reports',
    parent: 'financials',
    defaultEnabled: false,
  },
  {
    key: 'customReports',
    label: 'Custom Reports',
    description: 'Saved queries and ad-hoc SQL reports.',
    category: 'reports',
    defaultEnabled: false,
  },
  {
    key: 'routeReports',
    label: 'Load / Route Sheets',
    description: 'Driver + load sheet generation and route snapshot.',
    category: 'operations',
    defaultEnabled: true,
  },
  // Batch pre-pick — enables the "Unpicked Only" send option. Key matches the
  // agent's feature flag (route_reports.batch_pick) that gates the batch. OFF =
  // only the original "All Orders" send. Requires RouteBatch config (ASKI
  // creds/paths) on the agent. Opt-in per tenant (Devash).
  {
    key: 'route_reports.batch_pick',
    label: 'Load / Route Sheets → Batch Pre-Pick',
    description: 'Turns on the "Unpicked Only" load-sheet send: before emailing, the load sheet shows only the outstanding quantities and the batch marks those orders picked in Advantage (ASKI import + lock + pick, run in the background). Off = only the original "All Orders" send. Requires the RouteBatch config on the agent.',
    category: 'operations',
    parent: 'routeReports',
    defaultEnabled: false,
  },
  // Single daily batch — a third send mode. One scheduled send per day
  // (Daily_Load_Sheet_And_Routes calendar event) that picks EVERY unpicked order,
  // driver 003 included, with Second Send's recipients + per-driver emails. While
  // ON the scheduler ignores First/Second events; OFF it ignores Daily events.
  // Separate from batch_pick: the agent batches when either flag is on.
  {
    key: 'route_reports.daily_batch',
    label: 'Load / Route Sheets → Single Daily Batch',
    description: 'Once a day (the "Daily Send" calendar event) the load sheet shows every unpicked order including truck 003, and the batch marks them all picked in Advantage. Replaces the First/Second sends while on — those calendar events are ignored. Emails go out like the Second Send (group + per-driver). Requires agent 3.17.2+ and the RouteBatch config on the agent.',
    category: 'operations',
    parent: 'routeReports',
    defaultEnabled: false,
  },
  // Truck-load tracking — the agent runs Devash's two TruckLoad scripts (formerly
  // Windows scheduled tasks): CREATE tomorrow's TruckLoadHeader/TruckLoadTran in the
  // evening, UPDATE Delivered/OnTruck every 10 min 6 AM–9 PM. Schedule lives in the
  // agent's TruckLoad config section.
  {
    key: 'route_reports.truck_load',
    label: 'Load / Route Sheets → Truck Load Tracking',
    description: 'The agent builds tomorrow\'s truck loads (TruckLoadHeader / TruckLoadTran) each evening — 7:30 PM Mon–Thu and Sat, 6:30 PM Sun — and updates Delivered / On Truck from invoices every 10 minutes, 6 AM–9 PM. Turn off the matching Windows scheduled tasks before enabling. Requires agent 3.18.0+.',
    category: 'operations',
    parent: 'routeReports',
    defaultEnabled: false,
  },
  {
    key: 'production',
    label: 'Production',
    description: 'Batch recipe + production planning module (replicated from GeniBatch). Phase 1: Items + Recipes CRUD against per-tenant local storage. Off by default — enable per tenant when ready.',
    category: 'operations',
    defaultEnabled: false,
  },
  {
    key: 'quickbooks',
    label: 'QuickBooks Online',
    description: 'QBO integration — connect, sync, manage credentials.',
    category: 'integrations',
    defaultEnabled: false,
  },
  {
    key: 'systemStatus',
    label: 'System Status',
    description: 'Agent health, cache sync, recipe versions.',
    category: 'operations',
    defaultEnabled: true,
  },
  {
    key: 'userControl',
    label: 'My Account',
    description: 'Profile, roles, and effective feature visibility.',
    category: 'account',
    defaultEnabled: true,
  },
  {
    key: 'support',
    label: 'Support',
    description: 'Tenant-facing support ticket queue.',
    category: 'account',
    defaultEnabled: true,
  },
  {
    key: 'billing',
    label: 'Billing',
    description: 'Invoice history, totals, payment method (read-only).',
    category: 'account',
    defaultEnabled: false,
  },
  // ── Sysadmin (Auth0 role-gated, not in tenant features table) ──
  {
    key: 'systemAdmin',
    label: 'System Admin',
    description: 'Cross-tenant directory, features, audit. Gated by Auth0 GeniReports Admin role — not a per-tenant feature.',
    category: 'admin',
    defaultEnabled: false,
    sysadminOnly: true,
  },
];

const BY_KEY = new Map(FEATURE_CATALOG.map((f) => [f.key, f]));

export function getFeature(key: string): FeatureCatalogEntry | undefined {
  return BY_KEY.get(key);
}

export function getCatalogDefault(key: string): boolean {
  return BY_KEY.get(key)?.defaultEnabled ?? false;
}

/**
 * Resolve whether a feature is enabled for the current user. Precedence:
 *
 *   1. Per-user override  — if set (ON/OFF), it wins. Always.
 *   2. Tenant DB row      — if present, trust the agent's effective set
 *                            (which already factored in the tenant default).
 *   3. Catalog default    — fallback so brand-new tenants without any DB
 *                            rows still get a reasonable set of tiles.
 *
 * @param effective       set of feature keys from adminApi.getEffectiveFeatures
 *                        (used only in the no-override + tenant-row case)
 * @param dbEntryKeys     keys present in adminApi.getAllFeatures (tenant
 *                        defaults — proxy for "an admin touched this")
 * @param userOverrides   map of featureKey -> isEnabled for THIS user
 *                        from adminApi.getAllUserOverrides
 */
export function isFeatureEnabled(
  key: string,
  effective: Set<string>,
  dbEntryKeys: Set<string>,
  userOverrides: Map<string, boolean>,
): boolean {
  // (1) Per-user override is authoritative. The agent's getEffectiveFeatures
  // already merges this in, but only when a tenant row exists; if a user has
  // an override on a feature with no tenant row, that override would
  // otherwise get dropped on the floor here. Check it explicitly.
  if (userOverrides.has(key)) return userOverrides.get(key)!;
  // (2) Tenant has an explicit DB row → trust the agent's computed set.
  if (dbEntryKeys.has(key)) return effective.has(key);
  // (3) Nothing in the DB and no override → catalog default applies.
  return getCatalogDefault(key);
}
